import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { SwitchListPrintViewModel, PrintPass, PrintStep } from '../print/switchListPrintViewModel'
import { formatEnteredTrackFeet } from '../utils/formatEnteredTrackFeet'

/**
 * Switch list print sheet (Plan 26 Phase 1c-i, D7 client-side print).
 *
 * Mount with `active` → portals a full-screen print-preview overlay onto
 * `document.body`: the sheet renders on a paper-white card inside a dark
 * overlay, with a toolbar offering Print (calls `window.print()`, which
 * still isolates the sheet via `@media print` on desktop) and Close.
 *
 * The preview-first flow replaces the old auto-print because Android
 * Chrome/WebView's print pipeline can snapshot before the print-root
 * commit — the user then got the whole app as PDF. The overlay is
 * deterministic on every platform; on devices without a print service
 * the preview itself (or a screenshot) is the deliverable.
 *
 * B&W-safe: every step carries icon + text label, never color-only.
 * Field-use: each pass row has ☐ Done / time / initials blanks and room
 * for handwritten notes. Supersession: revision ID + verify URL printed
 * in the header — a stale sheet cannot hide its staleness.
 *
 * Pagination: `size: letter portrait`, `break-inside: avoid` on passes.
 * ("Page X of Y" margin boxes aren't supported by browser print engines;
 * the running document footer carries swl + revision instead.)
 *
 * TODO(tablet-refactor): the overlay chrome (dark backdrop, .swl-toolbar
 * with Print/Close, .swl-paper card, Escape handling) is generic
 * print-preview scaffolding — extract to
 * `shared/src/components/PrintPreviewOverlay` (props: title, onClose,
 * onPrint, children) when the mobile or shipper app prints. SheetBody
 * stays here as the domain-specific renderer.
 */

const PRINT_ROOT_ID = 'nyos-switch-list-print-root'

const PRINT_STYLES = `
  @media print {
    /* Hide the entire app shell — the portal root lives on document.body
       outside #root, so only the sheet prints. */
    #root, .app, body > *:not(#${PRINT_ROOT_ID}) { display: none !important; }
    #${PRINT_ROOT_ID} .swl-overlay {
      position: static !important;
      overflow: visible !important;
      background: none !important;
      padding: 0 !important;
    }
    #${PRINT_ROOT_ID} .swl-toolbar { display: none !important; }
    #${PRINT_ROOT_ID} .swl-paper { box-shadow: none !important; margin: 0 !important; max-width: none !important; width: 100% !important; }
    @page { size: letter portrait; margin: 0.5in; }
  }
`

interface Props {
  viewModel: SwitchListPrintViewModel
  active: boolean
  onDone?: () => void
}

export function SwitchListPrintSheet({ viewModel, active, onDone }: Props) {
  const [root, setRoot] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (!active) {
      setRoot(null)
      return
    }
    let el = document.getElementById(PRINT_ROOT_ID)
    let created = false
    if (!el) {
      el = document.createElement('div')
      el.id = PRINT_ROOT_ID
      document.body.appendChild(el)
      created = true
    }
    setRoot(el)

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDone?.()
    }
    window.addEventListener('keydown', onKey)

    return () => {
      window.removeEventListener('keydown', onKey)
      if (created) {
        el?.remove()
      }
    }
  }, [active]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!active || !root) {
    return null
  }

  return createPortal(
    <>
      <style>{PRINT_STYLES}</style>
      <div
        className="swl-overlay"
        onClick={(e) => {
          // Portal children still bubble through the React tree — without
          // this, every click inside the preview reaches the host drawer's
          // dismiss-on-backdrop handler and unmounts both layers.
          e.stopPropagation()
          // Backdrop tap (dark area outside the paper/toolbar) = close preview.
          if (e.target === e.currentTarget) onDone?.()
        }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100,
          overflowY: 'auto',
          background: 'rgba(0,0,0,0.72)',
          padding: '24px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <div className="swl-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', maxWidth: 760 }}>
          <span style={{ flex: 1, fontSize: 13, color: '#fff', fontWeight: 600 }}>
            Print Preview — {viewModel.documentTitle}
          </span>
          <button
            onClick={() => window.print()}
            style={{ padding: '8px 16px', borderRadius: 6, background: '#2563EB', color: '#fff', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer' }}
          >
            Print / Save PDF
          </button>
          <button
            onClick={() => onDone?.()}
            style={{ padding: '8px 16px', borderRadius: 6, background: '#374151', color: '#fff', fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer' }}
          >
            Close
          </button>
        </div>
        <div
          className="swl-paper"
          style={{
            width: '100%',
            maxWidth: 760,
            background: '#fff',
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}
        >
          <SheetBody viewModel={viewModel} />
        </div>
      </div>
    </>,
    root
  )
}

/** The pure renderer — also exported for markup-structure tests. */
export function SheetBody({ viewModel }: { viewModel: SwitchListPrintViewModel }) {
  const vm = viewModel
  return (
    <div className="swl-print" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#000', background: '#fff', padding: 8 }}>
      {/* Header — provenance-driven (EDI vs yard-authored) */}
      <header style={{ borderBottom: '2px solid #000', paddingBottom: 6, marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h1 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{vm.documentTitle}</h1>
          <span style={{ fontSize: 10 }}>Rev. {vm.revision != null ? `v${vm.revision}` : 'unrevisioned'}</span>
        </div>
        <div style={{ fontSize: 10, marginTop: 2 }}>
          {vm.woNumber ? `WO ${vm.woNumber}` : ''}
          {vm.swlNumber ? ` · SWL ${vm.swlNumber}` : ''}
          {' · Printed '}{new Date(vm.printedAt).toLocaleString()}
        </div>
        <div style={{ fontSize: 10, marginTop: 4 }}>
          {vm.provenance.kind === 'EDI' ? (
            <>
              <strong>EDI inbound</strong>
              {vm.provenance.trainSymbol ? ` · train ${vm.provenance.trainSymbol}` : ''}
              {vm.provenance.inboundCarrier ? ` · ${vm.provenance.inboundCarrier}` : ''}
              {vm.provenance.interchangeTrack ? ` · via ${vm.provenance.interchangeTrack}` : ''}
              {vm.provenance.ediDocReference ? ` · doc ${vm.provenance.ediDocReference}` : ''}
            </>
          ) : (
            <>
              {vm.provenance.locomotiveMark ? `Loco ${vm.provenance.locomotiveMark}` : ''}
              {vm.provenance.crewName ? ` · Crew ${vm.provenance.crewName}` : ''}
              {vm.provenance.requestedBy ? ` · Requested by ${vm.provenance.requestedBy}` : ''}
            </>
          )}
        </div>
        {vm.verifyUrl && (
          <div style={{ fontSize: 9, marginTop: 4, border: '1px solid #000', padding: '2px 4px', display: 'inline-block' }}>
            Verify this is current — check rev {vm.revision != null ? `v${vm.revision}` : '?'} at {vm.verifyUrl}
          </div>
        )}
      </header>

      {/* Metrics strip */}
      <section style={{ display: 'flex', gap: 12, flexWrap: 'wrap', borderBottom: '1px solid #000', paddingBottom: 6, marginBottom: 8 }}>
        {vm.metrics.map((m) => (
          <span key={m.label} style={{ fontSize: 10 }}>
            <strong>{m.label}:</strong> {m.value}
          </span>
        ))}
      </section>

      {/* Passes */}
      {vm.passes.map((pass) => (
        <PassBlock key={pass.passNumber} pass={pass} />
      ))}

      <footer style={{ borderTop: '1px solid #000', marginTop: 8, paddingTop: 4, fontSize: 9 }}>
        {vm.documentTitle}
        {vm.revision != null ? ` · rev v${vm.revision}` : ' · unrevisioned preview'}
        {' · NYOS'}
      </footer>
    </div>
  )
}

function PassBlock({ pass }: { pass: PrintPass }) {
  return (
    <section style={{ breakInside: 'avoid', border: '1px solid #000', marginBottom: 8 }}>
      {/* Repeating header behavior: passes keep their own header block so a
          pass never splits from its title across a page boundary. */}
      <div style={{ borderBottom: '1px solid #000', padding: '4px 6px', display: 'flex', justifyContent: 'space-between' }}>
        <strong style={{ fontSize: 12 }}>
          Pass {pass.passNumber}{pass.blocked ? ' — BLOCKED' : ''}
        </strong>
        {pass.destinationLabel && <span style={{ fontSize: 10 }}>→ {pass.destinationLabel}</span>}
      </div>
      <div style={{ padding: '4px 6px 2px', fontSize: 11 }}>{pass.summary}</div>
      {pass.statusReason && (
        <div style={{ padding: '0 6px 4px', fontSize: 10, fontStyle: 'italic' }}>{pass.statusReason}</div>
      )}
      {pass.warnings.map((w, wi) => (
        <div key={wi} style={{ padding: '0 6px 4px', fontSize: 10 }}>
          ⚠ {w}
        </div>
      ))}

      {pass.steps.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
          <tbody>
            {pass.steps.map((step, i) => (
              <StepRow key={i} step={step} />
            ))}
          </tbody>
        </table>
      )}

      {pass.cars.length > 0 && (
        <CarTable pass={pass} />
      )}
      {pass.unresolvedCarIds.length > 0 && (
        <div style={{ padding: '2px 6px 4px', fontSize: 9 }}>
          Cars not yet in yard inventory: {pass.unresolvedCarIds.length} (IDs withheld — see manifest)
        </div>
      )}

      {/* Field-use row — completion evidence + handwritten notes */}
      <div style={{ display: 'flex', gap: 18, padding: '4px 6px 6px', fontSize: 10, borderTop: '1px dashed #000' }}>
        <span>☐ Done</span>
        <span>Time: ________</span>
        <span>Initials: ________</span>
        <span style={{ flex: 1 }}>Notes: ______________________________</span>
      </div>
    </section>
  )
}

function StepRow({ step }: { step: PrintStep }) {
  return (
    <tr style={{ breakInside: 'avoid' }}>
      <td style={{ borderTop: '1px solid #ccc', padding: '3px 6px', width: 110, verticalAlign: 'top' }}>
        <strong>{step.type}</strong>
      </td>
      <td style={{ borderTop: '1px solid #ccc', padding: '3px 6px' }}>
        {step.label}
        {step.requiredSwitchState ? ` [${step.requiredSwitchState}]` : ''}
        {step.travelDistanceFeet != null ? ` — ${formatEnteredTrackFeet(step.travelDistanceFeet)}` : ''}
      </td>
    </tr>
  )
}

function CarTable({ pass }: { pass: PrintPass }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, marginTop: 4 }}>
      <thead>
        <tr>
          {['Car', 'Type', 'L/E', 'Len', 'Gross t', 'Hazmat', 'Destination'].map((h) => (
            <th key={h} style={{ border: '1px solid #000', padding: '2px 4px', textAlign: 'left' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {pass.cars.map((car) => (
          <tr key={car.reportingMark} style={{ breakInside: 'avoid' }}>
            <td style={{ border: '1px solid #000', padding: '2px 4px' }}>{car.reportingMark}</td>
            <td style={{ border: '1px solid #000', padding: '2px 4px' }}>{car.carType ?? '—'}</td>
            <td style={{ border: '1px solid #000', padding: '2px 4px' }}>{car.loadStatus ?? '—'}</td>
            <td style={{ border: '1px solid #000', padding: '2px 4px' }}>{car.lengthFeet ?? '—'}</td>
            <td style={{ border: '1px solid #000', padding: '2px 4px' }}>{car.grossWeightTons ?? '—'}</td>
            <td style={{ border: '1px solid #000', padding: '2px 4px' }}>
              {car.hazardous ? (car.hazmatClass ?? 'HAZ') : ''}
            </td>
            <td style={{ border: '1px solid #000', padding: '2px 4px' }}>{car.destination ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
